/*
 * dexDetailPipSlots.ts
 *
 * Turns a `PokemonDetailFrame` into the two things the PiP HUD renders while a
 * live scan is parked on a Pokémon GO **species detail page**:
 *
 *   - a species line, e.g. `CHARIZARD #6`
 *   - 16 grid slots carrying VARIANT LABELS (MAIN / SHINY / 100% / LUCKY / …)
 *     in FIXED positions, blank wherever the dex is not filled.
 *
 * This is the detail-page counterpart to `nexPip.ts`'s rolling dex-number grid.
 * On a scrolled Pokédex grid the useful thing to show is "which numbers did I
 * just add"; on a detail page the whole screen is ONE species, so the useful
 * thing is "which of this species' dexes do I already have" — the same shape the
 * user is looking at in-game, mirrored into the HUD.
 *
 * ── FIXED SLOTS from a canonical order (2026-07-21) ─────────────────────────
 * Every variant owns a PERMANENT slot — its index in `PIP_VARIANT_ORDER` — and
 * an un-owned variant leaves that slot BLANK. At four columns that lays the HUD
 * out as the same shape as the in-game page:
 *
 *     MAIN   SHINY  100%   LUCKY
 *     XXL    XXS    SHADOW PURIFY
 *     MEGA   GMAX
 *
 * so a Bulbasaur with no 100% and no XXS reads `MAIN SHINY ____ LUCKY / XXL
 * ____ SHADOW PURIFY` — the gaps land under the same cards the user is looking
 * at as silhouettes. That is the point: the HUD mirrors the screen.
 *
 * NOT the same thing as the ORIGINAL positional layout, which derived each
 * column from measured card GEOMETRY. That version had a real flaw — a card the
 * scanner could not read (below the fold, occluded) is absent from `variants`
 * entirely, so it could not hold a place it never reported, and every later
 * label shifted left. Here the slot map is fixed and independent of what was
 * read, so an unread variant and an un-owned one both render blank in the RIGHT
 * place, and nothing downstream of them moves.
 *
 * The order deliberately departs from Pokémon GO's own, which interleaves the
 * megas between XXS and G-Max and so splits the shadow/purified pair across
 * rows. A fixed map also means a given variant sits in the same place on EVERY
 * species — something the page's own layout cannot offer, since it shifts as
 * forms come and go.
 *
 * The HUD still never claims to know something the scanner didn't read: an
 * unread card is blank, exactly like an un-owned one.
 */
import type { PokemonDetailFrame } from "./pokemonDetailDexOcr";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/**
 * Grid geometry — mirrors NexPip3D.swift's `dexGridColumns` /
 * `detailGridRows`. FOUR rows (not the dex-number grid's three): a
 * Charizard-class species carries 12-16 scannable variants, and a 12-slot grid
 * silently dropped the tail. The dex-number grid keeps 3 rows; only detail mode
 * grows, which is why the Swift side reads a separate row count for it.
 */
export const PIP_SLOT_COLUMNS = 4;
export const PIP_SLOT_ROWS = 4;
export const PIP_SLOT_COUNT = PIP_SLOT_COLUMNS * PIP_SLOT_ROWS;

/**
 * Display label per scannable variant. Short enough to fit a quarter-width PiP
 * cell at the grid font size — the HUD clips rather than wraps, so these must
 * stay tight. "MAIN" for the base dex (the card Pokémon GO draws unlabelled)
 * and "100%" for `perfect` are the names the app uses in its own tab strip, so
 * the HUD reads the same as the Pokédex page the user just came from.
 *
 * Variants with no scan path (complete / shundo / shinyLucky) are deliberately
 * absent: they can never appear on a scanned card, so a label for them would be
 * unreachable code that implies a capability we do not have.
 */
export const PIP_VARIANT_LABELS: Partial<Record<PokedexVariant, string>> = {
  pokemon: "MAIN",
  shiny: "SHINY",
  perfect: "100%",
  lucky: "LUCKY",
  xxl: "XXL",
  xxs: "XXS",
  gmax: "GMAX",
  mega: "MEGA",
  shadow: "SHADOW",
  purified: "PURIFY",
};

/**
 * CANONICAL HUD SLOT MAP (2026-07-21). A variant's index here IS its permanent
 * grid slot; it is not merely a sort order. Independent of where the cards
 * happen to sit on the page.
 *
 * Grouped so a full set falls on meaningful row breaks at 4 columns:
 *
 *   row 1   MAIN  SHINY  100%  LUCKY    the base dex and its qualities
 *   row 2   XXL   XXS    SHADOW PURIFY  size pair, then the shadow/purified pair
 *   row 3   MEGA  GMAX                  the form-changing ones
 *
 * Pokémon GO's own page order does NOT group them this way (it interleaves the
 * megas between XXS and G-Max, splitting the shadow/purified pair onto a third
 * row), which is why this is an explicit list rather than screen geometry.
 * Pairs that mean something together stay together.
 *
 * A variant missing from this list cannot be emitted at all, so keep it in sync
 * with PIP_VARIANT_LABELS — the test asserts they cover exactly the same set.
 * APPENDING is safe; REORDERING moves labels under different cards, so only do
 * it deliberately.
 */
export const PIP_VARIANT_ORDER: readonly PokedexVariant[] = [
  "pokemon",
  "shiny",
  "perfect",
  "lucky",
  "xxl",
  "xxs",
  "shadow",
  "purified",
  "mega",
  "gmax",
];

export interface DetailPipSlots {
  /**
   * The HUD HEADLINE for this page, e.g. `CHARIZARD #6`, or null when the
   * species could not be named. Deliberately UNPADDED — the leading zeros in
   * Pokémon GO's own `0006` are decoration, and at headline size they read as
   * noise before the number that matters.
   */
  species: string | null;
  /**
   * Exactly PIP_SLOT_COUNT entries, indexed by PIP_VARIANT_ORDER; "" means blank
   * (not caught, or not read — the HUD does not distinguish, because it cannot
   * honestly).
   */
  slots: string[];
}

/**
 * Lay the frame's CAUGHT variants into their FIXED grid slots (see
 * PIP_VARIANT_ORDER), leaving a blank wherever the dex is not filled so the HUD
 * mirrors the shape of the page on screen.
 *
 * Only `state === "present"` variants produce a label. Card GEOMETRY is not
 * consulted at all: a variant's slot is its canonical index, so an unread card
 * cannot shift the ones after it.
 *
 * MEGA X and MEGA Y are two cards but one `mega` dex, so they share one slot —
 * a second "MEGA" would read as a rendering bug rather than as two facts.
 *
 * The returned array is always PIP_SLOT_COUNT long.
 */
export function detailPipSlots(frame: PokemonDetailFrame): DetailPipSlots {
  const slots = new Array<string>(PIP_SLOT_COUNT).fill("");

  if (frame.variants.length > 0) {
    const caught = new Set<PokedexVariant>();
    for (const obs of frame.variants) {
      if (obs.state === "present") caught.add(obs.variant);
    }
    // Each variant owns a PERMANENT slot — its index in PIP_VARIANT_ORDER — and
    // an un-owned one leaves that slot blank. See the FIXED SLOTS note in the
    // module header.
    for (let i = 0; i < PIP_VARIANT_ORDER.length && i < PIP_SLOT_COUNT; i++) {
      const variant = PIP_VARIANT_ORDER[i];
      if (!caught.has(variant)) continue;
      const label = PIP_VARIANT_LABELS[variant];
      if (label) slots[i] = label;
    }
  }

  const species =
    frame.dexNumber != null
      ? frame.name != null && frame.name.length > 0
        ? `${frame.name} #${frame.dexNumber}`
        : `#${frame.dexNumber}`
      : null;

  return { species, slots };
}

/** True when at least one slot carries a label — i.e. the HUD has something to show. */
export function hasAnySlot(slots: readonly string[]): boolean {
  return slots.some((s) => s.length > 0);
}
